package com.evoapps.evoshift.domain.budget

import com.evoapps.evoshift.domain.market.payroll.OpenShiftCostMode
import com.evoapps.evoshift.domain.market.payroll.OpenShiftCostSetting
import com.evoapps.evoshift.domain.market.payroll.PayrollRules
import com.evoapps.evoshift.domain.model.DailyRecord
import com.evoapps.evoshift.domain.model.AuditMetadata
import com.evoapps.evoshift.domain.model.FinancialEngagement
import com.evoapps.evoshift.domain.model.Shift
import com.evoapps.evoshift.domain.model.TimeRange
import com.evoapps.evoshift.domain.model.WorkplaceSettings
import com.evoapps.evoshift.domain.usecase.CalculateShiftCostUseCase
import kotlinx.datetime.DatePeriod
import kotlinx.datetime.DayOfWeek
import kotlinx.datetime.LocalDate
import kotlinx.datetime.plus

/*
 * Unassigned shifts in the planned labour cost. A month built from the skeleton starts with every
 * shift unassigned (userId null): demand nobody fills yet. Counting those at 0 made a new month look
 * cheap (the budget nearly untouched, every day "too few staff"), and handing a shift to nobody looked
 * like a saving although the shift still has to be worked. Nobody's own pay applies to them, so they
 * are an estimate: the month's average employer cost per hour (its cost snapshots), else the average
 * of the team's pay terms (a month with no assigned shift yet). Without either they are not priced,
 * and the screens say so instead of counting them as free.
 */

/** An unassigned shift that is demand still to be filled: not covered afterwards, not marked as not worked. */
val Shift.isUnfilledDemand: Boolean
    get() = userId == null && actualUserId == null && !notWorked

/** The month's unfilled shifts and their estimated cost ([costPerHour] null: not priced). */
data class OpenShiftsCost(
    val shifts: Int,
    val hours: Double,
    /** The employer cost per hour they are estimated at; null when none is known. */
    val costPerHour: Double?
) {
    /** Their estimated employer cost; null when they are not priced. */
    val estimate: Double? get() = costPerHour?.times(hours)

    val isPriced: Boolean get() = costPerHour != null

    companion object {
        val NONE = OpenShiftsCost(0, 0.0, null)

        fun of(shifts: List<Shift>, costPerHour: Double?): OpenShiftsCost {
            val open = shifts.filter { it.isUnfilledDemand }
            return OpenShiftsCost(open.size, open.sumOf(ShiftCostEstimate::hoursOf), costPerHour.takeIf { open.isNotEmpty() })
        }
    }
}

/**
 * The team's average employer cost per hour from their pay terms: each person's terms price an
 * ordinary 8-hour day shift (08:00–16:00 on the month's first working weekday) with the market's
 * payroll rules, as their shifts are priced (CalculateShiftCostUseCase), and the people's hourly
 * costs are averaged. Null without anyone's terms.
 */
object TeamCostRate {
    fun of(engagements: Collection<FinancialEngagement>, settings: WorkplaceSettings, year: Int, month: Int): Double? {
        if (engagements.isEmpty()) return null
        val day = generateSequence(LocalDate(year, month, 1)) { it.plus(DatePeriod(days = 1)) }
            .first { it.dayOfWeek != DayOfWeek.SATURDAY && it.dayOfWeek != DayOfWeek.SUNDAY }
        val reference = referenceShift(day)
        val monthHours = PayrollRules.of(settings.market).workingHours(year, month).toDouble()
        val rates = engagements.mapNotNull { engagement ->
            runCatching {
                val priced = CalculateShiftCostUseCase.price(reference, REFERENCE_USER, engagement, settings, monthHours)
                priced.ratesSnapshot.plannedHours.takeIf { it > 0 }?.let { priced.costs.employerCost / it }
            }.getOrNull()?.takeIf { it > 0 }
        }
        return rates.takeIf { it.isNotEmpty() }?.average()
    }

    private const val REFERENCE_USER = "team-average"

    private fun referenceShift(day: LocalDate) = Shift(
        id = "team-average",
        rosterId = "",
        date = day.toString(),
        dailyRecordId = "",
        requiredRole = null,
        userId = REFERENCE_USER,
        userDisplayName = null,
        actualUserId = null,
        isPublished = false,
        hasUnpublishedChanges = false,
        version = 0,
        plannedTime = TimeRange("${day}T08:00:00", "${day}T16:00:00"),
        actualTime = null,
        conflictMetadata = null,
        metadata = AuditMetadata("", "", "", ""),
        absenceId = null,
        tags = emptyList()
    )
}

/** SH-193: where an unassigned shift's estimated cost per hour came from. */
enum class OpenShiftRateSource {
    /** Last month's employer cost per hour worked here. */
    PREVIOUS_MONTH,
    /** This month's assigned shifts (their cost snapshots, or the team's pay terms before any). */
    THIS_MONTH,
    /** The rate set in the settings. */
    FIXED,
    /** Nothing to go by: the shifts are not priced (the settings can set a rate). */
    NONE
}

/** SH-193: the cost per hour unassigned shifts count at in the plan, and where it came from. */
data class OpenShiftRate(val perHour: Double?, val source: OpenShiftRateSource) {
    companion object {
        val NONE = OpenShiftRate(null, OpenShiftRateSource.NONE)
    }
}

/*
 * SH-193: unassigned shifts in the PLANNED labour cost, labour % and SPMH. The owner chooses per
 * workplace ([OpenShiftCostSetting]): automatic (last month's employer cost per hour worked here,
 * else this month's assigned shifts' average, else the rate set, else none and a hint to set one)
 * or a fixed rate. Their hours always count in the planned hours. The actual figures never hold
 * them: an unassigned shift was not worked by anyone; once assigned, its person's own rate applies.
 */
object OpenShiftRates {
    /** The rate [setting] gives with [previousMonth] (last month's cost per hour) and [thisMonth] (this month's). */
    fun resolve(setting: OpenShiftCostSetting, previousMonth: Double?, thisMonth: Double?): OpenShiftRate {
        val fixed = setting.validPerHour
        if (setting.mode == OpenShiftCostMode.FIXED) {
            return if (fixed != null) OpenShiftRate(fixed, OpenShiftRateSource.FIXED) else OpenShiftRate.NONE
        }
        usable(previousMonth)?.let { return OpenShiftRate(it, OpenShiftRateSource.PREVIOUS_MONTH) }
        usable(thisMonth)?.let { return OpenShiftRate(it, OpenShiftRateSource.THIS_MONTH) }
        if (fixed != null) return OpenShiftRate(fixed, OpenShiftRateSource.FIXED)
        return OpenShiftRate.NONE
    }

    /**
     * A month's employer cost per hour worked from its daily [records]: the actual labour cost ÷
     * the hours worked; a month nobody worked yet (no hours), its assigned shifts' planned cost ÷
     * their hours. Null without either.
     */
    fun ofMonth(records: Collection<DailyRecord>): Double? {
        val actualHours = records.sumOf { it.costs.actualHours }
        if (actualHours > 0) return usable(records.sumOf { it.costs.actualByContractType.values.sum() } / actualHours)
        val plannedHours = records.sumOf { it.costs.assignedPlannedHours }
        if (plannedHours > 0) return usable(records.sumOf { it.costs.plannedByContractType.values.sum() } / plannedHours)
        return null
    }

    private fun usable(rate: Double?): Double? = rate?.takeIf { !it.isNaN() && !it.isInfinite() && it > 0 }
}
